import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { 
  Briefcase, MapPin, Mail, Calendar, Clock, FileText, Eye, 
  Users, MessageCircle, Target, Award, CheckCircle, XCircle 
} from 'lucide-react';

const JobApplicationDetailModal = ({ 
  isOpen, 
  onClose, 
  application, 
  onStatusUpdate, 
  onNavigate 
}) => {
  if (!application) return null;

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'applied': 'badge-primary',
      'pending': 'badge-primary',
      'shortlisted': 'badge-warning',
      'accepted': 'badge-success',
      'rejected': 'badge-danger'
    };
    return statusMap[status] || 'badge-neutral';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto mx-4 p-0" data-testid="job-application-detail-modal">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-primary/10 to-purple-500/10 p-6 rounded-t-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-3">
              <div className="icon-container-purple">
                <Briefcase className="w-5 h-5 text-primary" />
              </div>
              Application Details
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="p-6 space-y-6">
          {/* Applicant Info Card */}
          <div className="card-premium p-5">
            <div className="flex items-start gap-4">
              <Avatar className="w-16 h-16 ring-2 ring-primary/20 ring-offset-2 ring-offset-background">
                <AvatarImage src={application.applicant?.profileImage} alt={application.applicant?.name} />
                <AvatarFallback className="text-lg bg-primary/10 text-primary font-semibold">
                  {application.applicant?.name?.[0] || 'A'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-bold text-foreground">{application.applicant?.name || 'Unknown Applicant'}</h3>
                    <p className="text-sm text-muted-foreground">@{application.applicant?.username || 'unknown'}</p>
                    
                    <div className="flex flex-wrap gap-2 mt-3">
                      {application.applicant?.email && (
                        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-full">
                          <Mail className="w-3 h-3" /> {application.applicant.email}
                        </span>
                      )}
                      {application.applicant?.location && (
                        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded-full">
                          <MapPin className="w-3 h-3" /> {application.applicant.location}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className={getStatusBadgeClass(application.status)}>
                    {application.status === 'pending' ? 'Applied' : application.status}
                  </span>
                </div>
                
                {application.applicant?.bio && (
                  <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{application.applicant.bio}</p>
                )}
                
                <div className="flex flex-wrap gap-2 mt-3">
                  {application.applicant?.persona && (
                    <Badge variant="outline" className="text-xs">{application.applicant.persona}</Badge>
                  )}
                  {application.applicant?.sports?.map((sport, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">{sport}</Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Position Applied For */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              <Target className="w-4 h-4 text-primary" />
              Position Applied For
            </h4>
            <div className="bg-accent/30 rounded-xl p-4">
              <h5 className="font-bold text-lg text-foreground">{application.jobTitle}</h5>
              <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
                {application.jobType && (
                  <span className="flex items-center gap-1.5">
                    <Briefcase className="w-3.5 h-3.5" /> {application.jobType}
                  </span>
                )}
                {application.jobLocation && (
                  <span className="flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" /> {application.jobLocation}
                  </span>
                )}
                {application.jobSalary && (
                  <span className="flex items-center gap-1.5">
                    £ {application.jobSalary}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Cover Letter */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              Cover Letter
            </h4>
            {application.coverLetter && application.coverLetter !== '[object Object]' ? (
              <div className="bg-primary/5 rounded-xl p-4 border border-primary/10">
                <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{application.coverLetter}</p>
              </div>
            ) : (
              <div className="bg-muted/30 rounded-xl p-4 text-center">
                <p className="text-sm text-muted-foreground">No cover letter provided</p>
              </div>
            )}
          </div>

          {/* Resume/CV */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Resume / CV</h4>
            <div className="bg-muted/30 rounded-xl p-4">
              {application.cvUrl ? (
                <div className="flex items-center gap-4">
                  <div className="icon-container-purple">
                    <FileText className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">Resume attached</p>
                    <p className="text-xs text-muted-foreground">Click to view or download</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => window.open(application.cvUrl, '_blank')}
                    className="btn-primary"
                  >
                    <Eye className="w-3.5 h-3.5 mr-1.5" /> View CV
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-2">No resume uploaded</p>
              )}
            </div>
          </div>

          {/* Application Timeline */}
          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              Applied: {application.createdAt ? new Date(application.createdAt).toLocaleDateString('en-US', { 
                year: 'numeric', month: 'short', day: 'numeric'
              }) : 'Unknown'}
            </span>
            {application.updatedAt && application.updatedAt !== application.createdAt && (
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                Updated: {new Date(application.updatedAt).toLocaleDateString('en-US', { 
                  year: 'numeric', month: 'short', day: 'numeric'
                })}
              </span>
            )}
          </div>

          {/* Skills & Stats */}
          {((application.applicant?.stats && Object.keys(application.applicant.stats).length > 0) || 
            (application.applicant?.skills && application.applicant.skills.length > 0)) && (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                <Award className="w-4 h-4 text-primary" />
                Skills & Stats
              </h4>
              {application.applicant?.skills && application.applicant.skills.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {application.applicant.skills.map((skill, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">{skill}</Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="pt-4 border-t border-border">
            <h4 className="text-sm font-semibold mb-3">Update Status</h4>
            <div className="flex flex-wrap gap-2">
              {application.status !== 'shortlisted' && application.status !== 'accepted' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-purple-400 border-purple-400/30 hover:bg-purple-400/10"
                  onClick={() => onStatusUpdate(application._id, 'shortlisted')}
                >
                  <Target className="w-3.5 h-3.5 mr-1.5" /> Shortlist
                </Button>
              )}
              {application.status !== 'accepted' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-primary border-primary/30 hover:bg-primary/10"
                  onClick={() => onStatusUpdate(application._id, 'accepted')}
                >
                  <CheckCircle className="w-3.5 h-3.5 mr-1.5" /> Accept
                </Button>
              )}
              {application.status !== 'rejected' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-destructive border-destructive/30 hover:bg-destructive/10"
                  onClick={() => onStatusUpdate(application._id, 'rejected')}
                >
                  <XCircle className="w-3.5 h-3.5 mr-1.5" /> Reject
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  onNavigate(`/profile/${application.applicant?.username || application.applicantId}`);
                  onClose();
                }}
              >
                <Users className="w-3.5 h-3.5 mr-1.5" /> View Profile
              </Button>
              <Button
                size="sm"
                className="btn-primary"
                onClick={() => {
                  onNavigate(`/messages?userId=${application.applicantId}`);
                  onClose();
                }}
              >
                <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> Message
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter className="p-4 border-t border-border bg-muted/30">
          <Button variant="outline" onClick={() => onClose(false)} className="w-full sm:w-auto">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default JobApplicationDetailModal;
